import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
	Grid,
	Typography,
	TextField,
	Chip,
	FormControlLabel,
	Switch,
	IconButton,
	List,
	ListItem,
	ListItemText,
	ListItemSecondaryAction,
	Button
} from '@material-ui/core'
import { AttachFile, Delete as DeleteIcon } from '@material-ui/icons'
import TextBox from '../../common/TextBox'
import RichTextEditor from '../../common/RichTextEditor'
import ButtonSaved from '../../common/Buttons/ButtonSaved'
import TimeLineEmailHistory from './TimeLineEmailHistory'
import { useDispatch, useSelector } from 'react-redux'
import { addEmailMessageHistory, addUsersEmailMessageHistory } from '../../../store/services/serviceActions'
import { loadAllUsers } from '../../../store/users/UsersActions'
import { UserTypes } from '../../../store/helpers/AppConstants'
import { Autocomplete } from '@material-ui/lab'
import { isNullOrEmpty, isValidEmailFormat, stripHtmlTags } from '../../../store/helpers/StringHelper'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB
const MAX_TOTAL_SIZE = 30 * 1024 * 1024 // 30 MB

const EmailCommentHistory = (props) => {
	const { service, userProfile, onResendEmail, isSavingService, isAuthorizedForCreatingEmail } = props
	const dispatch = useDispatch()
	const allUsers = useSelector((state) => state.users.get('allUsers'))
	const loadingUsers = useSelector((state) => state.users.get('isLoadingUsers'))
	const [isIspUsersMode, setIsIspUsersMode] = useState(true)
	const [selectedUsers, setSelectedUsers] = useState([])
	const [emailMessageModel, setEmailMessageModel] = useState({
		message: '',
		email: '',
		dateAttention: null
	})
	const [selectedFiles, setSelectedFiles] = useState([])
	const fileInputRef = useRef(null)
	const [fileError, setFileError] = useState('')
	const [errors, setErrors] = useState({
		email: '',
		message: '',
		users: ''
	})

	const totalFileSize = useMemo(() => selectedFiles.reduce((sum, f) => sum + f.size, 0), [selectedFiles])
	const isTotalSizeExceeded = totalFileSize > MAX_TOTAL_SIZE

	useEffect(() => {
		const loadData = async () => {
			const codesToLoad = [
				service?.Dispatcher?.Code,
				service?.TechAssigned?.Code,
				service?.ServiceProvider?.Code,
				userProfile?.Account?.Code,
				service?.Customer?.Code,
				service?.SalesRepresentative?.Code,
				'IMB'
			]
				.filter(Boolean) // elimina valores nulos o undefined
				.filter((v, i, a) => a.indexOf(v) === i) // elimina duplicados
			await dispatch(loadAllUsers(codesToLoad))
		}
		loadData()
	}, [dispatch, userProfile, service])

	const ispUsers = useMemo(() => {
		if (!allUsers || allUsers.size === 0) return []
		const mappedUsers = allUsers
			.entrySeq()
			.toArray()
			.map(([_, user]) => ({
				id: user.get('Id') || user.get('UserName'),
				name: user.get('Name') || user.get('UserName'),
				email: user.get('Email') || user.get('UserName'),
				userName: user.get('UserName')
			}))
		const uniqueEmails = new Set()
		const usersWithoutDuplicates = mappedUsers.filter((user) => {
			const normalizedEmail = user.email?.toLowerCase()
			if (uniqueEmails.has(normalizedEmail)) return false
			uniqueEmails.add(normalizedEmail)
			return true
		})
		const sortedUsersByName = usersWithoutDuplicates.sort((a, b) => a.name.localeCompare(b.name))
		return sortedUsersByName
	}, [allUsers])

	const validateFields = () => {
		const newErrors = {
			email: '',
			message: '',
			users: ''
		}
		if (isNullOrEmpty(emailMessageModel.message) || !stripHtmlTags(emailMessageModel.message).trim()) {
			newErrors.message = 'Message is required'
		}
		if (isIspUsersMode) {
			if (selectedUsers.length === 0) {
				newErrors.users = 'At least one user must be selected'
			}
		} else {
			if (isNullOrEmpty(emailMessageModel.email)) {
				newErrors.email = 'Email is required'
			} else if (!isValidEmailFormat(emailMessageModel.email)) {
				newErrors.email = 'One or more email addresses are invalid'
			}
		}
		setErrors(newErrors)
		return newErrors.email === '' && newErrors.message === '' && newErrors.users === ''
	}

	const onPropertyChange = (event) => {
		const { name, value } = event.target
		setEmailMessageModel({
			...emailMessageModel,
			[name]: value
		})
		setErrors((prev) => ({
			...prev,
			[name]: ''
		}))
	}

	const onMessageChange = (html) => {
		setEmailMessageModel((prev) => ({ ...prev, message: html }))
		setErrors((prev) => ({ ...prev, message: '' }))
	}

	const handleUserModeChange = (event) => {
		setIsIspUsersMode(event.target.checked)
		setErrors({ email: '', message: '', users: '' })
		setSelectedUsers([])
		setEmailMessageModel((prev) => ({ ...prev, email: '' }))
	}

	const handleFileSelect = (event) => {
		const files = Array.from(event.target.files)
		const oversizedFiles = files.filter((f) => f.size > MAX_FILE_SIZE)
		if (oversizedFiles.length > 0) {
			const names = oversizedFiles.map((f) => f.name).join(', ')
			setFileError(`The following file(s) exceed the 10 MB limit and were not added: ${names}`)
			const validFiles = files.filter((f) => f.size <= MAX_FILE_SIZE)
			if (validFiles.length > 0) setSelectedFiles((prev) => [...prev, ...validFiles])
		} else {
			setFileError('')
			setSelectedFiles((prev) => [...prev, ...files])
		}
		if (fileInputRef.current) fileInputRef.current.value = ''
	}

	const handleFileRemove = (index) => {
		setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
		setFileError('')
	}

	const addEmailMessageHistoryHandler = () => {
		if (!validateFields()) return
		if (isIspUsersMode) {
			const selectedUsersEmails = ispUsers.filter((user) => selectedUsers.includes(user.id)).map((user) => user.email)
			const selectedUsersUserNames = ispUsers.filter((user) => selectedUsers.includes(user.id)).map((user) => user.userName)
			dispatch(addUsersEmailMessageHistory(service, emailMessageModel.message, selectedUsersEmails, selectedUsersUserNames, selectedFiles))
				.then(() => {
					setEmailMessageModel({ message: '', email: '', dateAttention: null })
					setSelectedUsers([])
					setSelectedFiles([])
				})
				.catch((errorMessage) => {
					console.error('Error al guardar el historial de mensaje (ISP User):', errorMessage)
				})
		} else {
			dispatch(addEmailMessageHistory(service, emailMessageModel.message, emailMessageModel.email, selectedFiles))
				.then(() => {
					setEmailMessageModel({ message: '', email: '', dateAttention: null })
					setSelectedFiles([])
				})
				.catch((errorMessage) => {
					console.error('Error al guardar el historial de mensaje (No Users):', errorMessage)
				})
		}
	}

	let isAuthorizadoForEditedData = isAuthorizedForCreatingEmail !== undefined ? isAuthorizedForCreatingEmail : true
	let isActiveAllComments = userProfile ? userProfile.userType === UserTypes.ACTIVE_DIRECTORY : false
	let allEmailsHistory = filterEmailsByUser(service?.EmailsHistories ?? [], isActiveAllComments, userProfile)

	return (
		<Grid container spacing={2}>
			{isAuthorizadoForEditedData && (
				<>
					<Grid item xs={12}>
						<FormControlLabel
							control={<Switch checked={isIspUsersMode} onChange={handleUserModeChange} name='userModeSwitch' color='primary' />}
							label={isIspUsersMode ? 'ISP User' : 'No User'}
						/>
					</Grid>
					<Grid item xs={12}>
						{isIspUsersMode ? (
							<Autocomplete
								multiple
								options={ispUsers}
								getOptionLabel={(option) => `${option.name} (${option.email})`}
								value={ispUsers.filter((user) => selectedUsers.includes(user.id))}
								onChange={(event, newValue) => {
									setSelectedUsers(newValue.map((user) => user.id))
									setErrors((prev) => ({ ...prev, users: '' }))
								}}
								filterSelectedOptions
								disableCloseOnSelect
								renderTags={(tagValue, getTagProps) =>
									tagValue
										.slice(0, 10)
										.map((option, index) => (
											<Chip
												key={option.id}
												label={option.name}
												size='small'
												sx={{ fontSize: '0.75rem' }}
												{...getTagProps({ index })}
											/>
										))
								}
								renderInput={(params) => (
									<TextField
										{...params}
										label='Select User'
										variant='outlined'
										margin='dense'
										error={!!errors.users}
										helperText={
											errors.users ||
											(loadingUsers
												? 'Loading ISP user...'
												: ispUsers.length > 0
												? 'Select one or more ISP user to send the message'
												: 'No ISP user available')
										}
									/>
								)}
								disabled={loadingUsers || ispUsers.length === 0}
							/>
						) : (
							<TextBox
								active={true}
								fullWidth
								name='email'
								variant='outlined'
								margin='dense'
								label='Email'
								type='email'
								placeholder='William@gmail.com, anthony@imberacooling.com'
								helperText={errors.email || 'Enter email addresses separated by commas'}
								error={!!errors.email}
								onChange={onPropertyChange}
								value={emailMessageModel.email}
								disabled={!isAuthorizadoForEditedData}
							/>
						)}
					</Grid>
					<Grid item xs={12}>
						<Typography component='label' htmlFor='email-message-editor' variant='caption' color='textSecondary' style={{ display: 'block', marginBottom: 4 }}>
							Message
						</Typography>
						<RichTextEditor
							id='email-message-editor'
							name='message'
							value={emailMessageModel.message}
							onChange={onMessageChange}
							disabled={!isAuthorizadoForEditedData}
						/>
						<Typography variant='caption' color={errors.message ? 'error' : 'textSecondary'} style={{ display: 'block', marginTop: 4 }}>
							{errors.message || 'Your message will be sent to all listed email addresses.'}
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<input type='file' multiple ref={fileInputRef} onChange={handleFileSelect} style={{ display: 'none' }} />
						<Button
							variant='outlined'
							size='small'
							startIcon={<AttachFile />}
							onClick={() => fileInputRef.current && fileInputRef.current.click()}
						>
							Attach Files
						</Button>
						<Typography variant='caption' color='textSecondary' style={{ display: 'block', marginTop: 4 }}>
							Max 10 MB per file, 30 MB total.
						</Typography>
						{fileError && (
							<Typography variant='caption' color='error' style={{ display: 'block', marginTop: 4 }}>
								{fileError}
							</Typography>
						)}
						{isTotalSizeExceeded && (
							<Typography variant='caption' color='error' style={{ display: 'block', marginTop: 4 }}>
								Total attachment size exceeds 30 MB. Please remove some files before sending.
							</Typography>
						)}
						{selectedFiles.length > 0 && (
							<List dense>
								{selectedFiles.map((file, index) => (
									<ListItem key={index}>
										<ListItemText primary={file.name} secondary={`${(file.size / 1024).toFixed(1)} KB`} />
										<ListItemSecondaryAction>
											<IconButton edge='end' size='small' onClick={() => handleFileRemove(index)}>
												<DeleteIcon fontSize='small' />
											</IconButton>
										</ListItemSecondaryAction>
									</ListItem>
								))}
							</List>
						)}
					</Grid>
					<Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
						<ButtonSaved
							title='Send Email'
							onSavedClick={addEmailMessageHistoryHandler}
							isSaving={isSavingService || isTotalSizeExceeded}
						/>
					</Grid>
				</>
			)}
			<Grid item xs={12}>
				<Typography variant='body2' color='textSecondary'>
					Create comments and send emails to the customer.
				</Typography>
			</Grid>
			<Grid item xs={12}>
				<div
					style={{
						margin: '20px 0',
						height: '1px',
						backgroundColor: '#e0e0e0'
					}}
				/>
			</Grid>
			<Grid item xs={12}>
				<TimeLineEmailHistory
					emailsHistory={allEmailsHistory}
					serviceToken={service?.Token ?? ''}
					onResendEmail={onResendEmail}
					isAuthorizedForCreatingEmail={isAuthorizadoForEditedData}
					service={service}
				/>
			</Grid>
		</Grid>
	)
}

function filterEmailsByUser(emailsHistory, isInternalUser, userProfile) {
	if (isInternalUser) {
		return emailsHistory
	}
	const userEmail = userProfile?.email || userProfile?.Email
	const filteredEmails = emailsHistory.filter((email) => {
		const isCreatedByUser = email.UserName === userProfile?.userName
		let isSentToUser = false
		if (email.EmailTo && userEmail) {
			const emailRecipients = email.EmailTo.split(',').map((e) => e.trim().toLowerCase())
			isSentToUser = emailRecipients.includes(userEmail.toLowerCase())
		}
		const shouldInclude = isCreatedByUser || isSentToUser
		return shouldInclude
	})
	return filteredEmails
}

export default EmailCommentHistory
